import React, { useEffect, useRef, useState } from 'react';

const Hero = () => {
  const canvasRef = useRef(null);
  const [typedText, setTypedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const fullText = "Transforming Businesses with AI Solutions";
  const typingSpeed = 100; // ms per character
  const cursorBlinkSpeed = 530; // ms per blink
  
  // Neural network animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    let connections = [];
    
    // Particle class
    class Particle {
      constructor(x, y, ctx) {
        this.x = x;
        this.y = y;
        this.size = Math.random() * 3 + 2;
        this.speedX = Math.random() * 1 - 0.5;
        this.speedY = Math.random() * 1 - 0.5;
        this.color = '#00ffff';
        this.pulse = 0;
        this.pulseSpeed = 0.02 + Math.random() * 0.03;
        this.isHighlighted = Math.random() > 0.85; // Some nodes are highlighted
        this.ctx = ctx;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        // Bounce off edges
        if (this.x > canvas.width || this.x < 0) this.speedX *= -1;
        if (this.y > canvas.height || this.y < 0) this.speedY *= -1;

        // Pulse animation
        this.pulse += this.pulseSpeed;
        if (this.pulse > 1 || this.pulse < 0) this.pulseSpeed *= -1;
      }

      draw() {
        const pulseSize = this.isHighlighted ? 
          this.size + Math.abs(Math.sin(this.pulse)) * 4 : 
          this.size;
          
        const glowIntensity = this.isHighlighted ? 
          0.5 + Math.abs(Math.sin(this.pulse)) * 0.5 : 
          0.2;
        
        // Draw glow
        const gradient = this.ctx.createRadialGradient(
          this.x, this.y, 0, 
          this.x, this.y, pulseSize * 2
        );
        
        gradient.addColorStop(0, `rgba(0, 255, 255, ${glowIntensity})`);
        gradient.addColorStop(1, 'rgba(0, 255, 255, 0)');
        
        this.ctx.fillStyle = gradient;
        this.ctx.beginPath();
        this.ctx.arc(this.x, this.y, pulseSize * 2, 0, Math.PI * 2);
        this.ctx.fill();
        
        // Draw particle
        this.ctx.fillStyle = this.isHighlighted ? '#ffffff' : this.color;
        this.ctx.beginPath();
        this.ctx.arc(this.x, this.y, pulseSize, 0, Math.PI * 2);
        this.ctx.fill();
      }
    }

    // Connection class
    class Connection {
      constructor(particleA, particleB, ctx) {
        this.particleA = particleA;
        this.particleB = particleB;
        this.distance = 0;
        this.active = false;
        this.activeTime = 0;
        this.maxActiveTime = 100 + Math.random() * 150;
        this.ctx = ctx;
      }

      update() {
        this.distance = Math.sqrt(
          Math.pow(this.particleA.x - this.particleB.x, 2) + 
          Math.pow(this.particleA.y - this.particleB.y, 2)
        );
        
        // Connection is active if distance is within range
        if (this.distance < 150) {
          if (!this.active && Math.random() < 0.01) {
            this.active = true;
            this.activeTime = 0;
          }
        } else {
          this.active = false;
        }

        // Handle active connections
        if (this.active) {
          this.activeTime++;
          if (this.activeTime > this.maxActiveTime) {
            this.active = false;
            this.activeTime = 0;
          }
        }
      }

      draw() {
        if (this.distance < 150) {
          const opacity = this.active ? 
            Math.min(1, this.activeTime / 20) * (1 - this.activeTime / this.maxActiveTime) * 0.8 : 
            Math.max(0, 1 - this.distance / 150) * 0.2;
            
          this.ctx.strokeStyle = `rgba(0, 255, 255, ${opacity})`;
          this.ctx.lineWidth = this.active ? 1.5 : 0.5;
          
          this.ctx.beginPath();
          this.ctx.moveTo(this.particleA.x, this.particleA.y);
          this.ctx.lineTo(this.particleB.x, this.particleB.y);
          this.ctx.stroke();
          
          // Draw pulse if connection is active
          if (this.active && this.activeTime < this.maxActiveTime * 0.8) {
            const progress = this.activeTime / (this.maxActiveTime * 0.8);
            const pulseX = this.particleA.x + (this.particleB.x - this.particleA.x) * progress;
            const pulseY = this.particleA.y + (this.particleB.y - this.particleA.y) * progress;
            
            this.ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
            this.ctx.beginPath();
            this.ctx.arc(pulseX, pulseY, 2, 0, Math.PI * 2);
            this.ctx.fill();
          }
        }
      }
    }
    
    // Set canvas dimensions
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      initParticles();
    };

    // Initialize particles
    function initParticles() {
      particles = [];
      connections = [];
      
      // Create particles
      const particleCount = Math.min(40, Math.floor(canvas.width * canvas.height / 15000));
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle(
          Math.random() * canvas.width,
          Math.random() * canvas.height,
          ctx
        ));
      }
      
      // Create connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          connections.push(new Connection(particles[i], particles[j], ctx));
        }
      }
    }

    // Handle window resize
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    // Animation loop with throttling for better performance
    let lastFrame = 0;
    const fps = 30; // Target 30 FPS for better performance
    const fpsInterval = 1000 / fps;
    
    function animate(timestamp) {
      animationFrameId = requestAnimationFrame(animate);
      
      // Throttle the frame rate
      const elapsed = timestamp - lastFrame;
      if (elapsed < fpsInterval) return;
      
      lastFrame = timestamp - (elapsed % fpsInterval);
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw connections
      connections.forEach(connection => {
        connection.update();
        connection.draw();
      });
      
      // Update and draw particles
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
    }
    
    animate(0);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Typing animation effect
  useEffect(() => {
    // Start typing animation
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setTypedText(fullText.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, typingSpeed);
    
    // Setup cursor blinking
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, cursorBlinkSpeed);
    
    return () => {
      clearInterval(typingInterval);
      clearInterval(cursorInterval);
    };
  }, [fullText, typingSpeed, cursorBlinkSpeed]);

  // Floating AI icon animation
  const FloatingIcon = ({ icon, delay, duration, top, left }) => {
    return (
      <div 
        className="floating-icon" 
        style={{
          animationDelay: `${delay}s`,
          animationDuration: `${duration}s`,
          top: `${top}%`,
          left: `${left}%`
        }}
      >
        {icon}
      </div>
    );
  };

  return (
    <section id="home" className="hero">
      <canvas ref={canvasRef} className="neural-network-canvas"></canvas>
      <div className="hero-gradient-1"></div>
      <div className="hero-gradient-2"></div>
      <div className="hero-gradient-3"></div>
      
      {/* Floating AI-themed icons */}
      <FloatingIcon 
        icon={<span role="img" aria-label="robot">🤖</span>} 
        delay={0.5} 
        duration={15} 
        top={25} 
        left={15} 
      />
      <FloatingIcon 
        icon={<span role="img" aria-label="brain">🧠</span>} 
        delay={2.5} 
        duration={18} 
        top={70} 
        left={80} 
      />
      <FloatingIcon 
        icon={<span role="img" aria-label="lightning">⚡</span>} 
        delay={1.2} 
        duration={12} 
        top={20} 
        left={80} 
      />
      <FloatingIcon 
        icon={<span role="img" aria-label="rocket">🚀</span>} 
        delay={3} 
        duration={16} 
        top={80} 
        left={20} 
      />
      <FloatingIcon 
        icon={<span role="img" aria-label="chart">📊</span>} 
        delay={0.8} 
        duration={14} 
        top={40} 
        left={90} 
      />
      
      <div className="container">
        <div className="hero-content fade-in">
          <h2 className="typing-text">
            {typedText}
            <span className={`cursor ${showCursor ? 'visible' : 'hidden'}`}>|</span>
          </h2>
          <p className="fade-in-delay">We help companies leverage the power of AI to drive innovation, efficiency, and growth.</p>
          <a href="#services" className="btn btn-primary pulse-button">Get Started</a>
        </div>
      </div>
    </section>
  );
};

export default Hero; 